import { bus } from "../core/bus";

const MODE_LABELS: Record<string, string> = {
  "white-hot": "WHT-HOT",
  "black-hot": "BLK-HOT",
  "eo-green":  "EO-GRN",
  "daylight":  "DAYLIGHT",
};

export type AircraftInstruments = {
  altitude: number;
  speedPct: number;
  altBand: "LOW" | "MED" | "HIGH";
  hull: number; // 0..100
  flares: number;
  missileInbound: boolean;
  stalled: boolean;
  flakThreat: boolean; // any AA sites alive this mission
};

export class OrbitHud {
  private readonly el: HTMLElement;
  private target: "squad" | "enemy" = "squad";
  private sensorMode = "daylight";
  private inst: AircraftInstruments = {
    altitude: 0, speedPct: 0, altBand: "MED", hull: 100, flares: 3,
    missileInbound: false, stalled: false, flakThreat: false,
  };
  private lastHtml = "";
  private readonly onOrbitTargetChanged = ({ target }: { target: "squad" | "enemy" }): void => {
    this.target = target;
    this.render();
  };
  private readonly onSensorModeChanged = ({ mode }: { mode: string }): void => {
    this.sensorMode = mode;
    this.render();
  };

  constructor() {
    const el = document.querySelector("[data-slot='sensor']") as HTMLElement | null;
    if (!el) throw new Error("Missing HUD sensor slot");
    this.el = el;
    this.el.style.textAlign = "right";
    this.el.style.lineHeight = "1.8";

    bus.on("orbit:targetChanged", this.onOrbitTargetChanged);
    bus.on("sensor:modeChanged", this.onSensorModeChanged);

    this.render();
  }

  updateInstruments(inst: AircraftInstruments): void {
    this.inst = inst;
    this.render();
  }

  render(): void {
    const label = this.target === "squad" ? "SQUAD" : "ENEMY CLUSTER";
    const tColor = this.target === "squad" ? "rgba(80,160,255,0.9)" : "rgba(255,80,60,0.9)";
    const modeLabel = MODE_LABELS[this.sensorMode] ?? this.sensorMode;
    const i = this.inst;
    const bandColor = i.altBand === "LOW" ? "rgba(255,120,90,0.95)" : i.altBand === "HIGH" ? "rgba(160,220,255,0.9)" : "rgba(200,255,210,0.9)";
    const hullColor = i.hull > 60 ? "rgba(200,255,210,0.9)" : i.hull > 30 ? "rgba(255,200,110,0.95)" : "rgba(255,90,70,0.95)";
    const warning = i.missileInbound
      ? `<br><span style="font-size:12px;color:#ff5040;letter-spacing:0.15em">▲ MISSILE — SPACE = FLARES</span>`
      : i.stalled
        ? `<br><span style="font-size:12px;color:#ffb35c;letter-spacing:0.15em">▲ AIRSPEED</span>`
        : "";
    const flakLine = i.flakThreat
      ? ` &nbsp; <span style="opacity:0.7">FLARES <span style="color:rgba(200,255,210,0.9)">${i.flares}</span></span>`
      : "";
    const html = `
      <span style="font-size:10px;opacity:0.5">ORBIT TARGET</span><br>
      <span style="font-size:13px;color:${tColor}">${label}</span><br>
      <span style="font-size:10px;opacity:0.5">SENSOR: <span style="color:rgba(180,255,200,0.9)">${modeLabel}</span></span><br>
      <span style="font-size:10px;opacity:0.5">ALT <span style="color:${bandColor}">${Math.round(i.altitude)}m ${i.altBand}</span> &nbsp; SPD <span style="color:rgba(200,255,210,0.9)">${Math.round(i.speedPct)}%</span></span><br>
      <span style="font-size:10px;opacity:0.5">HULL <span style="color:${hullColor}">${Math.round(i.hull).toString().padStart(3, "0")}</span>${flakLine}</span>${warning}<br>
      <span style="font-size:9px;opacity:0.4">[Q/E] target  [W/S] speed  [Z/X] alt  [TAB] sensor  [SPACE] flares</span>
    `;
    // Skip the DOM write (and the style/layout recalc it forces) on the many
    // frames where the rounded readouts haven't changed.
    if (html === this.lastHtml) return;
    this.lastHtml = html;
    this.el.innerHTML = html;
  }

  dispose(): void {
    bus.off("orbit:targetChanged", this.onOrbitTargetChanged);
    bus.off("sensor:modeChanged", this.onSensorModeChanged);
    this.el.innerHTML = "";
  }
}
